'use client';

import PropTypes from 'prop-types';
import React from 'react';
import { lsgIcons } from '../../lib/icons/lsgIconsData';
import '../../styles/icons/LsgIcon.css';

/**
 * LsgIcon - Componente para renderizar iconos LSG
 * 
 * Los iconos LSG son iconos corporativos con dos capas:
 * - CONTAINER: Capa de fondo (opcional, desactivado por defecto)
 * - ICON: Capa del icono (por defecto currentColor: toma el color del texto)
 * 
 * @example
 * ```jsx
 * <LsgIcon name="AI" size={64} />
 * <LsgIcon name="Calendar" showContainer={true} containerColor="#FF5733" iconColor="#FFFFFF" />
 * <LsgIcon name="Globe" size={32} iconColor="#003d78" />
 * ```
 */
const LsgIcon = ({
  name,
  size = 48,
  // Brand: Lean Blue container; the glyph follows the surrounding text colour
  // (Trust Navy on light, light on dark, the active colour in a selected item).
  containerColor = '#0099FF',
  iconColor = 'currentColor',
  showContainer = false,
  className = '',
  style = {},
  title,
  ...props
}) => {
  const iconData = lsgIcons[name];

  if (!iconData) {
    console.warn(`LsgIcon: Icon "${name}" not found. Available icons:`, Object.keys(lsgIcons).join(', '));
    return null;
  }

  const { container, containerTransform, icon: iconPaths } = iconData;

  return (
    <svg
      className={`lsg-icon lsg-icon-${name.toLowerCase()} ${className}`}
      width={size}
      height={size}
      viewBox="0 0 400 400"
      style={style}
      aria-hidden={!title}
      role={title ? 'img' : 'presentation'}
      {...props}
    >
      {title && <title>{title}</title>}
      
      {/* CONTAINER Layer */}
      {showContainer && container && (
        <g id="layer-container">
          <path
            d={container}
            fill={containerColor}
            fillOpacity="1"
            fillRule="nonzero"
            stroke="none"
            transform={containerTransform}
          />
        </g>
      )}

      {/* ICON Layer */}
      <g id="layer-icon">
        {Array.isArray(iconPaths) ? (
          iconPaths.map((pathData, index) => (
            <path
              key={index}
              d={pathData.d}
              fill={iconColor}
              fillOpacity="1"
              fillRule="nonzero"
              stroke="none"
              transform={pathData.transform}
            />
          ))
        ) : (
          <path
            d={iconPaths.d}
            fill={iconColor}
            fillOpacity="1"
            fillRule="nonzero"
            stroke="none"
            transform={iconPaths.transform}
          />
        )}
      </g>
    </svg>
  );
};

LsgIcon.propTypes = {
  /** Nombre del icono (ej: 'AI', 'Calendar', 'Globe') */
  name: PropTypes.string.isRequired,
  
  /** Tamaño del icono en píxeles */
  size: PropTypes.number,
  
  /** Color del contenedor/fondo */
  containerColor: PropTypes.string,
  
  /** Color del icono */
  iconColor: PropTypes.string,
  
  /** Mostrar la capa del contenedor */
  showContainer: PropTypes.bool,
  
  /** Clase CSS adicional */
  className: PropTypes.string,
  
  /** Estilos inline adicionales */
  style: PropTypes.object,
  
  /** Título accesible para el icono */
  title: PropTypes.string,
};

/**
 * Hook para obtener la lista de iconos disponibles
 */
export const useLsgIcons = () => {
  return React.useMemo(() => ({
    all: Object.keys(lsgIcons),
    count: Object.keys(lsgIcons).length,
    has: (name) => name in lsgIcons,
    search: (term) => {
      const searchTerm = term.toLowerCase();
      return Object.keys(lsgIcons).filter(iconName =>
        iconName.toLowerCase().includes(searchTerm)
      );
    }
  }), []);
};

/**
 * LsgIconButton - Botón con icono LSG
 */
export const LsgIconButton = ({
  icon,
  onClick,
  disabled = false,
  size = 48,
  className = '',
  title,
  ariaLabel,
  ...props
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`lsg-icon-button ${className} ${disabled ? 'lsg-icon-button--disabled' : ''}`}
      aria-label={ariaLabel || title || icon}
      title={title}
      {...props}
    >
      <LsgIcon name={icon} size={size} title={title} />
    </button>
  );
};

LsgIconButton.propTypes = {
  /** Nombre del icono */
  icon: PropTypes.string.isRequired,
  
  /** Función a ejecutar al hacer clic */
  onClick: PropTypes.func,
  
  /** Estado deshabilitado */
  disabled: PropTypes.bool,
  
  /** Tamaño del icono */
  size: PropTypes.number,
  
  /** Clase CSS adicional */
  className: PropTypes.string,
  
  /** Título del botón */
  title: PropTypes.string,
  
  /** Etiqueta ARIA para accesibilidad */
  ariaLabel: PropTypes.string,
};

/**
 * LsgIconGrid - Grid para mostrar múltiples iconos
 */
export const LsgIconGrid = ({
  icons = [],
  iconSize = 48,
  columns = 4,
  gap = 16,
  onIconClick,
  className = '',
  showLabels = true,
  ...props
}) => {
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: `${gap}px`,
  };

  return (
    <div className={`lsg-icon-grid ${className}`} style={gridStyle} {...props}>
      {icons.map((iconName) => (
        <div
          key={iconName}
          className="lsg-icon-grid-item"
          onClick={() => onIconClick && onIconClick(iconName)}
          style={{ cursor: onIconClick ? 'pointer' : 'default' }}
        >
          <LsgIcon name={iconName} size={iconSize} />
          {showLabels && (
            <div className="lsg-icon-grid-item-label">{iconName}</div>
          )}
        </div>
      ))}
    </div>
  );
};

LsgIconGrid.propTypes = {
  /** Array de nombres de iconos a mostrar */
  icons: PropTypes.arrayOf(PropTypes.string),
  
  /** Tamaño de cada icono */
  iconSize: PropTypes.number,
  
  /** Número de columnas en el grid */
  columns: PropTypes.number,
  
  /** Espacio entre iconos */
  gap: PropTypes.number,
  
  /** Función a ejecutar al hacer clic en un icono */
  onIconClick: PropTypes.func,
  
  /** Clase CSS adicional */
  className: PropTypes.string,
  
  /** Mostrar etiquetas con los nombres */
  showLabels: PropTypes.bool,
};

export default LsgIcon;
